import type { AuthProvider } from '@refinedev/core';
import i18n from '../i18n.js';
import { tx } from '../i18n/translate.js';
import {
  buildLoginEntryUrl,
  buildPlatformLoginUrl,
  consumeCrossAppTokenFromUrl,
  setPortalAppSwitchingSession,
} from './app-navigation.js';
import { TOKEN_KEY, USER_KEY, crestHttp } from './client.js';
import {
  clearStoredCrmModeOverride,
  normalizeStoredCrmMode,
  resolveUserCrmMode,
} from './crm-mode-preference.js';
import {
  LAST_LOGIN_ENTRY_KEY,
  type LoginEntryPath,
  currentLoginEntryPath,
  normalizeLoginEntryPath,
  storedLoginEntryPath,
} from './login-entry.js';
import { notificationStore } from './notification-store.js';

export const LAST_LOGIN_USERNAME_KEY = 'crest_last_login_username';
export const DINGTALK_NONCE_KEY = 'crest_dingtalk_nonce';
export const DINGTALK_LOGIN_ENTRY_KEY = 'crest_dingtalk_login_entry';
export const POST_AUTH_REDIRECT_KEY = 'crest_post_auth_redirect';
export const DINGTALK_MANAGED_FLOW_KEY = 'crest_dingtalk_managed_flow';
const RELEASE_ROUTE_RELOAD_KEY = 'crest_release_route_reload_target';
export { LAST_LOGIN_ENTRY_KEY, type LoginEntryPath } from './login-entry.js';

export interface AdminScope {
  scopeType: 'super' | 'org' | 'department';
  scopeValue?: string;
}

export type CrmMode = 'domestic' | 'overseas';
export type CrmModeScope = CrmMode | 'both';

export interface AuthUser {
  id: string;
  tenantId: string;
  domainName: string;
  fullName: string;
  businessUnitId?: string;
  businessUnitIds?: string[];
  roleViewBusinessUnitIds?: string[];
  roleViewUserIds?: string[];
  businessUnitName?: string;
  roleIds: string[];
  roleNames: string[];
  platformRoleNames?: string[];
  privileges: Record<string, number>;
  adminScopes?: AdminScope[];
  canModifyLockedData?: boolean;
  canViewDomesticOverseasData?: boolean;
  visibleButtonKeys?: string[];
  hiddenButtonKeys?: string[];
  appNames?: string[];
  /** 用户是否归属海外市场部 BU (或其后代). 海外用户默认看美元价格 */
  crmModeScope?: CrmModeScope;
  defaultCrmMode?: CrmMode;
  availableCrmModes?: CrmMode[];
  isOverseas?: boolean;
  /** 用户默认货币 (海外 USD / 国内 CNY) */
  defaultCurrency?: 'CNY' | 'USD';
  /** 用户默认货币符号 (海外 $ / 国内 ¥) */
  defaultCurrencySymbol?: '¥' | '$';
}

export function hasSuperAdminScope(user?: Partial<Pick<AuthUser, 'adminScopes'>> | null): boolean {
  return user?.adminScopes?.some((scope) => scope.scopeType === 'super') === true;
}

interface DingTalkPublicConfig {
  client_id: string;
  redirect_uri: string;
  response_type: 'code';
  scope: string;
  state: string;
  nonce: string;
}

interface DingTalkConfigResponse {
  config: DingTalkPublicConfig;
}

type LoginResult =
  | { success: true; redirectTo: string }
  | { success: false; error: { name: string; message: string } };

function meRequestOptions() {
  return { params: { _ts: Date.now() } };
}

export const authProvider: AuthProvider = {
  async login({ username, password }) {
    const loginEntryPath = currentLoginEntryPath();
    try {
      const { data } = await crestHttp.post('/auth/login', { username, password });
      return await storeAuthenticatedSession(data.accessToken, username, '/', loginEntryPath);
    } catch (err: unknown) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      const e = err as { response?: { data?: { message?: string } } };
      return {
        success: false,
        error: {
          name: tx('hardcoded:api.auth.provider.df7d98cc'),
          message:
            e?.response?.data?.message ??
            tx(
              e?.response
                ? 'hardcoded:api.auth.provider.8f243339'
                : 'hardcoded:api.auth.provider.serviceUnavailable',
            ),
        },
      };
    }
  },

  async logout() {
    const redirectTo = '/';
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setPortalAppSwitchingSession(false);
    notificationStore.reset();
    clearStoredCrmModeOverride();
    // M68: 登出时清除手动语言偏好, 下次登录重新按 BU 同步
    clearLanguageManualOverride();
    if (typeof window !== 'undefined') window.location.assign(redirectTo);
    return { success: true, redirectTo };
  },

  async check() {
    consumeCrossAppTokenFromUrl();
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      return { authenticated: false, redirectTo: buildLoginEntryUrl(storedLoginEntryPath()) };
    }
    try {
      const meResponse = await crestHttp.get<AuthUser>('/me', meRequestOptions());
      const me = meResponse.data;
      localStorage.setItem(USER_KEY, JSON.stringify(me));
      normalizeStoredCrmMode(me);
      // M68: 首次加载 / 刷新页面时, 若用户未手动切换过语言, 按当前角色 UI 模式同步
      syncLanguageFromUser(me);
      synchronizePlatformReleaseRoute(meResponse.headers?.['x-crest-route-platform']);
      return { authenticated: true };
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      return {
        authenticated: false,
        redirectTo: buildLoginEntryUrl(storedLoginEntryPath()),
      };
    }
  },

  async onError(error) {
    if (error?.statusCode === 401) {
      return { logout: true, redirectTo: buildPlatformLoginUrl() };
    }
    return {};
  },

  async getIdentity() {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      const me = JSON.parse(raw) as AuthUser;
      return {
        ...me,
        name: me.fullName,
        avatar: undefined,
      };
    } catch {
      return null;
    }
  },

  async getPermissions() {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return [];
    try {
      const me = JSON.parse(raw) as AuthUser;
      return me.privileges;
    } catch {
      return {};
    }
  },
};

export async function startDingTalkLogin(options?: {
  loginEntryPath?: LoginEntryPath;
}): Promise<void> {
  const { data } = await crestHttp.get<DingTalkConfigResponse>('/auth/dingtalk/config');
  const { config } = data;
  const loginEntryPath = options?.loginEntryPath ?? currentLoginEntryPath();
  sessionStorage.setItem(DINGTALK_NONCE_KEY, config.nonce);
  sessionStorage.setItem(DINGTALK_LOGIN_ENTRY_KEY, loginEntryPath);
  sessionStorage.setItem(POST_AUTH_REDIRECT_KEY, currentPostAuthRedirect());
  window.location.assign(buildDingTalkAuthorizeUrl(config));
}

export async function completeDingTalkLogin(input: {
  code: string;
  state: string;
}): Promise<LoginResult> {
  try {
    const nonce = sessionStorage.getItem(DINGTALK_NONCE_KEY) ?? '';
    const { data } = await crestHttp.post('/auth/dingtalk/session', { ...input, nonce });
    const redirectTo = normalizePostAuthRedirect(
      sessionStorage.getItem(POST_AUTH_REDIRECT_KEY) ?? '/',
    );
    const loginEntryPath = normalizeLoginEntryPath(
      sessionStorage.getItem(DINGTALK_LOGIN_ENTRY_KEY),
    );
    const result = await storeAuthenticatedSession(
      data.accessToken,
      undefined,
      redirectTo,
      loginEntryPath,
    );
    if (!result.success) return result;
    // 个人待办只需要身份登录；可选的个人数据授权不能打断消息卡片的办理路径。
    if (redirectTo.startsWith('/work/operations/')) return result;
    try {
      const managed = await crestHttp.post<{ flowId: string }>(
        '/auth/dingtalk/authorization/start',
        { source: 'login' },
      );
      if (!managed.data.flowId) return result;
      sessionStorage.setItem(DINGTALK_MANAGED_FLOW_KEY, managed.data.flowId);
      return {
        ...result,
        redirectTo: `/app/account/settings?section=connections&returnTo=${encodeURIComponent(redirectTo)}`,
      };
    } catch {
      // 完整 DWS 授权是可选步骤；平台未启用或用户稍后处理都不能阻断登录。
      return result;
    }
  } catch (err: unknown) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    const e = err as { response?: { data?: { message?: string } } };
    return {
      success: false,
      error: {
        name: tx('hardcoded:api.auth.provider.df7d98cc'),
        message: e?.response?.data?.message ?? tx('legacy:pages.loginpage.dingtalkFailed'),
      },
    };
  } finally {
    sessionStorage.removeItem(DINGTALK_NONCE_KEY);
    sessionStorage.removeItem(DINGTALK_LOGIN_ENTRY_KEY);
    sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
  }
}

export async function sendEmailLoginCode(email: string): Promise<{
  ok: true;
  expiresInSeconds: number;
  resendAfterSeconds: number;
}> {
  const { data } = await crestHttp.post('/auth/email-code/send', { email });
  return data;
}

export async function loginWithEmailCode(input: {
  email: string;
  code: string;
  loginEntryPath?: LoginEntryPath;
}): Promise<LoginResult> {
  try {
    const loginEntryPath = input.loginEntryPath ?? currentLoginEntryPath();
    const { data } = await crestHttp.post('/auth/email-code/login', {
      email: input.email,
      code: input.code,
    });
    return await storeAuthenticatedSession(data.accessToken, input.email, '/', loginEntryPath);
  } catch (err: unknown) {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    const e = err as { response?: { data?: { message?: string } } };
    return {
      success: false,
      error: {
        name: tx('hardcoded:api.auth.provider.df7d98cc'),
        message: e?.response?.data?.message ?? tx('hardcoded:api.auth.provider.8f243339'),
      },
    };
  }
}

function buildDingTalkAuthorizeUrl(config: DingTalkPublicConfig): string {
  const search = new URLSearchParams({
    client_id: config.client_id,
    response_type: config.response_type,
    prompt: 'consent',
    scope: config.scope,
    state: config.state,
    redirect_uri: config.redirect_uri,
  });
  return `https://login.dingtalk.com/oauth2/auth?${search.toString()}`;
}

async function storeAuthenticatedSession(
  accessToken: string,
  username?: unknown,
  redirectTo = '/',
  loginEntryPath: LoginEntryPath = '/login',
): Promise<LoginResult> {
  localStorage.setItem(TOKEN_KEY, accessToken);
  const meResponse = await crestHttp.get<AuthUser>('/me', meRequestOptions());
  // 登录态只由 Platform /me 判定。CRM 在 MF 挂载后使用按 audience
  // 签发的应用令牌加载自己的身份上下文，不能拿 Platform 会话令牌
  // 直接请求 CRM /me，否则子服务的 401 会错误打断统一登录。
  const me = meResponse.data;
  localStorage.setItem(USER_KEY, JSON.stringify(me));
  localStorage.setItem(LAST_LOGIN_ENTRY_KEY, loginEntryPath);
  const loginName =
    typeof username === 'string' && username.trim() ? username.trim() : me.domainName;
  if (loginName) localStorage.setItem(LAST_LOGIN_USERNAME_KEY, loginName);
  normalizeStoredCrmMode(me);
  // M68: 登录时按当前角色 UI 模式自动设置语言 (海外→英文, 国内→中文)
  // 用户登录后可通过 LanguageSwitcher 手动切换, 切换后 localStorage 持久化
  syncLanguageFromUser(me);
  setPortalAppSwitchingSession(loginEntryPath === '/crest');
  synchronizePlatformReleaseRoute(meResponse.headers?.['x-crest-route-platform']);
  return {
    success: true,
    redirectTo: resolvePasswordPortalLanding(me, redirectTo, loginEntryPath),
  };
}

/**
 * `/crest` 是所有用户共用的账号密码入口，不能固定落到管理员应用中心。
 * 普通用户登录后进入自己获权的业务应用；超级管理员仍进入应用中心。
 */
function resolvePasswordPortalLanding(
  user: AuthUser,
  fallback: string,
  loginEntryPath: LoginEntryPath,
): string {
  if (loginEntryPath !== '/crest') return fallback;
  if (hasSuperAdminScope(user)) return '/app/admin/app-center';

  const allowed = new Set((user.appNames ?? []).map((name) => name.trim().toLowerCase()));
  if (allowed.has('crm')) return '/app/crm';
  if (allowed.has('agent')) return '/app/agent';
  if (allowed.has('operations') || allowed.has('reporting')) return '/app/operations';
  if (allowed.has('admin')) return '/app/admin/app-center';
  return fallback;
}

function synchronizePlatformReleaseRoute(target: unknown): void {
  const current = import.meta.env.VITE_RELEASE_ENVIRONMENT;
  if (
    typeof target !== 'string' ||
    (target !== 'legacy' && target !== 'crest') ||
    (current !== 'legacy' && current !== 'crest') ||
    typeof window === 'undefined'
  ) {
    return;
  }
  if (target === current) {
    sessionStorage.removeItem(RELEASE_ROUTE_RELOAD_KEY);
    return;
  }
  if (sessionStorage.getItem(RELEASE_ROUTE_RELOAD_KEY) === target) return;
  sessionStorage.setItem(RELEASE_ROUTE_RELOAD_KEY, target);
  window.location.reload();
}

function currentPostAuthRedirect(): string {
  if (typeof window === 'undefined') return '/';
  const { pathname, search, hash } = window.location;
  if (
    pathname.startsWith('/login') ||
    pathname === '/platform' ||
    pathname === '/crest' ||
    pathname === '/crm' ||
    pathname === '/agent' ||
    pathname === '/operate' ||
    pathname === '/erp'
  ) {
    return '/';
  }
  return `${pathname}${search}${hash}` || '/';
}

function normalizePostAuthRedirect(value: string): string {
  const normalized = value.trim();
  if (
    !normalized ||
    normalized.startsWith('/login') ||
    normalized === '/platform' ||
    normalized === '/crest' ||
    normalized === '/crm' ||
    normalized === '/agent' ||
    normalized === '/operate' ||
    normalized === '/erp'
  ) {
    return '/';
  }
  return normalized.startsWith('/') ? normalized : '/';
}

// ── M68: 语言自动同步 ──────────────────────────────────────────
// 用户手动切换 key — LanguageSwitcher 切换时写入, 标记用户主动选择过语言
const LANG_MANUAL_KEY = 'i18nManualOverride';

/**
 * 按当前角色 UI 模式同步 i18n 语言:
 *   - overseasUser / 海外角色 / 海外 BU → 'en' (英文 + USD)
 *   - domesticUser / 国内角色 / 国内 BU → 'zh' (中文 + CNY)
 *
 * 仅在用户**未手动切换过语言**时自动同步;
 * 手动切换后 (localStorage LANG_MANUAL_KEY 存在) 不再覆盖, 尊重用户选择.
 */
function syncLanguageFromUser(me: AuthUser): void {
  // 用户曾手动切换 → 跳过自动同步
  if (localStorage.getItem(LANG_MANUAL_KEY)) return;

  const targetLng = resolveUserCrmMode(me) === 'overseas' ? 'en' : 'zh';
  const currentLng = i18n.language ?? 'zh';
  if (!currentLng.startsWith(targetLng)) {
    i18n.changeLanguage(targetLng);
  }
}

/**
 * 标记用户手动选择了语言 (由 LanguageSwitcher 调用).
 * 一旦标记, syncLanguageFromUser 不再自动覆盖.
 */
export function markLanguageManualOverride(): void {
  localStorage.setItem(LANG_MANUAL_KEY, '1');
}

/**
 * 清除手动语言偏好 (logout 时调用, 让下次登录重新按 BU 同步)
 */
export function clearLanguageManualOverride(): void {
  localStorage.removeItem(LANG_MANUAL_KEY);
}
